import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  FileText, Stethoscope, AlertTriangle, CheckCircle2, Loader2,
  CalendarClock, Pill, Repeat, Brain,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Public, token-gated report page. The 48-hour signed JWT in the `t=`
   query param IS the auth — no login required. Reached by tapping the
   "Download your report" link in the patient's WhatsApp/SMS message.
*/
export default function ReportView() {
  const { cid } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) {
        setState({ loading: false, error: "This report link is missing its token.", data: null });
        return;
      }
      try {
        const r = await axios.get(`${API}/report/${encodeURIComponent(cid)}`, { params: { t: token } });
        if (alive) setState({ loading: false, error: null, data: r.data });
      } catch (err) {
        const msg = err?.response?.data?.detail || "Could not load report.";
        if (alive) setState({ loading: false, error: msg, data: null });
      }
    })();
    return () => { alive = false; };
  }, [cid, token]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="report-home-link">
            <img src="/icons/nav-brain.png" alt="NeuroOpinion" className="w-8 h-8 object-contain" width={32} height={32} />
            <span className="font-heading font-semibold text-sm">NeuroOpinion India</span>
          </Link>
          <span className="ml-auto text-[11px] text-slate-500">Secure report viewer · 48-hour link</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 sm:py-10">
        {state.loading && (
          <div className="flex items-center justify-center py-20 text-slate-500" data-testid="report-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your report…
          </div>
        )}

        {!state.loading && state.error && (
          <Card className="p-6 border-red-200 bg-red-50/60" data-testid="report-error">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-heading font-semibold text-red-800">Can't open this report</div>
                <p className="text-sm text-red-700 mt-1 leading-relaxed">{state.error}</p>
                <div className="mt-4 text-xs text-slate-600 leading-relaxed">
                  Report links expire 48 hours after they're sent. To get a fresh link, log in to your patient
                  dashboard and re-download from your case timeline.
                </div>
                <div className="mt-3">
                  <Link to="/login?as=patient" className="text-blue-700 underline text-sm" data-testid="report-error-login-link">
                    Log in to view your report →
                  </Link>
                </div>
              </div>
            </div>
          </Card>
        )}

        {!state.loading && state.data && (
          <ReportBody data={state.data} />
        )}
      </main>

      <footer className="text-center text-[11px] text-slate-400 py-6">
        © NeuroOpinion India. This report is private and intended only for {state.data?.case?.patient_name || "the patient"}.
      </footer>
    </div>
  );
}

function ReportBody({ data }) {
  const { case: caseDoc, opinion, uploaded_scans = [] } = data;
  return (
    <div className="space-y-5" data-testid="report-body">
      <Card className="p-5 sm:p-6 border-slate-200">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#0B5ED7]">Second opinion ready</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold mt-1" data-testid="report-title">
              Hi {caseDoc.patient_name || "there"} —
              <span className="block text-slate-700 text-base sm:text-lg font-normal mt-1">
                Your report from <b>{caseDoc.doctor_name}</b> is ready.
              </span>
            </h1>
          </div>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-semibold text-emerald-700" data-testid="report-status-pill">
            <CheckCircle2 className="w-3.5 h-3.5"/> {caseDoc.short_id}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <Field icon={CalendarClock} label="Submitted" value={new Date(caseDoc.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} />
          <Field icon={Stethoscope} label="Doctor" value={caseDoc.doctor_name} />
          <Field icon={FileText} label="Priority" value={caseDoc.priority === "video" ? "Video consult" : (caseDoc.priority === "urgent" ? "Emergency" : "Standard")} />
          <Field icon={Brain} label="Tags" value={(caseDoc.symptom_tags || []).join(", ") || "—"} />
        </div>
      </Card>

      {opinion ? (
        <>
          <Section icon={Stethoscope} title="Diagnosis summary" testid="report-diagnosis">
            <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{opinion.diagnosis_summary || "—"}</p>
          </Section>
          <Section icon={CheckCircle2} title="Recommendations" testid="report-recommendations">
            <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{opinion.recommendations || "—"}</p>
          </Section>
          {opinion.prescription ? (
            <Section icon={Pill} title="Prescription" testid="report-prescription">
              <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{opinion.prescription}</p>
            </Section>
          ) : null}
          {opinion.follow_up_required ? (
            <Card className="p-4 border-amber-200 bg-amber-50">
              <div className="flex items-start gap-2 text-sm text-amber-900">
                <Repeat className="w-4 h-4 mt-0.5 shrink-0" />
                <span><b>Follow-up suggested.</b> Please book a follow-up consultation in 4–6 weeks or sooner if symptoms worsen.</span>
              </div>
            </Card>
          ) : null}
        </>
      ) : (
        <Card className="p-5 border-amber-200 bg-amber-50">
          <div className="text-sm text-amber-900">The opinion document is not yet available. Please refresh in a few moments.</div>
        </Card>
      )}

      <Card className="p-5 sm:p-6 border-slate-200">
        <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">What you originally shared</div>
        <p className="mt-2 text-sm text-slate-700 whitespace-pre-line leading-relaxed">{caseDoc.symptoms || "—"}</p>
        {uploaded_scans.length > 0 && (
          <div className="mt-4 text-[11px] text-slate-500" data-testid="report-scans-count">
            {uploaded_scans.length} scan{uploaded_scans.length === 1 ? "" : "s"} uploaded (DICOM / images / PDFs).
          </div>
        )}
      </Card>

      <div className="flex flex-col sm:flex-row gap-2">
        <Button asChild className="h-11 bg-[#0B5ED7] hover:bg-[#084BB0]" data-testid="report-print-btn">
          <a href="#" onClick={(e) => { e.preventDefault(); window.print(); }}>Save / Print as PDF</a>
        </Button>
        <Button asChild variant="outline" className="h-11" data-testid="report-login-btn">
          <Link to="/login?as=patient">View my full case timeline</Link>
        </Button>
      </div>

      <p className="text-[11px] text-slate-400 leading-relaxed">
        This second opinion is for informational purposes and does not replace an in-person evaluation.
        Always discuss with your treating doctor before changing any prescribed medication.
      </p>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-2.5">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="mt-1 text-xs font-semibold text-slate-800 truncate" title={value}>{value || "—"}</div>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <Card className="p-5 sm:p-6 border-slate-200" data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#0B5ED7]">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <div className="mt-2.5">{children}</div>
    </Card>
  );
}
